import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Download, Share2, Radar, Loader2, FileText, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { api, errMsg, eur, fmtDateTime, money, downloadFile } from '../api';
import { usePortal } from '../PortalAuth';
import { savePref } from '../prefs';
import { PageHeader, Card, SectionTitle, Spinner, goldBtn, ghostBtn } from '../ui';
import ControlChainSection from '../report/ControlChainSection';
import { KV, RawData, RatingSection, NegativeSection, FinancialSection, PeopleSection, GroupSection } from '../report/Sections';
import DocumentsPanel from '../report/DocumentsPanel';
import FromReportDialog from '../aml/FromReportDialog';
import PropertyButton from '../report/PropertyButton';

function Overview({ report }) {
  const s = report.companySummary || {};
  const alt = report.alternateSummary || {};
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card className="p-6 lg:col-span-2"><SectionTitle>Dati anagrafici</SectionTitle><KV data={{ ...alt, mainActivity: s.mainActivity ? `${s.mainActivity.code} · ${s.mainActivity.description}` : undefined }} /></Card>
      <div className="space-y-4">
        <Card className="p-6"><p className="text-[11px] uppercase tracking-[0.15em] text-[#5A5F67] mb-2">Fatturato ultimo esercizio</p><p className="font-mono text-2xl" data-testid="report-turnover">{money(s.latestTurnoverFigure?.value, s.latestTurnoverFigure?.currency)}</p></Card>
        <Card className="p-6"><p className="text-[11px] uppercase tracking-[0.15em] text-[#5A5F67] mb-2">Patrimonio netto</p><p className="font-mono text-2xl">{money(s.latestShareholdersEquityFigure?.value, s.latestShareholdersEquityFigure?.currency)}</p></Card>
        <Card className="p-6"><p className="text-[11px] uppercase tracking-[0.15em] text-[#5A5F67] mb-2">Dipendenti</p><p className="font-mono text-2xl">{report.otherInformation?.employeesInformation?.[0]?.numberOfEmployees ?? '—'}</p></Card>
      </div>
    </div>
  );
}

export default function ReportPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refresh } = usePortal();
  const [doc, setDoc] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [cover, setCoverState] = useState(() => localStorage.getItem('bond_pdf_cover') || 'a');
  const setCover = (v) => { savePref('pdf_cover', v); setCoverState(v); };
  const [withGraph, setWithGraphState] = useState(() => localStorage.getItem('bond_pdf_graph') === '1');
  const setWithGraph = (v) => { savePref('pdf_graph', v); setWithGraphState(v); };
  const [amlDone, setAmlDone] = useState(false);
  const [withAml, setWithAml] = useState(true);

  useEffect(() => {
    api.get(`/aml/checks?report_id=${id}`).then((r) => setAmlDone(r.data.some((c) => c.status === 'done'))).catch(() => {});
  }, [id]);

  useEffect(() => {
    api.get(`/cs/reports/${id}`).then((r) => setDoc(r.data)).catch((e) => { toast.error(errMsg(e)); navigate('/portal/report'); });
  }, [id, navigate]);

  if (!doc) return <Spinner label="Caricamento report…" />;
  const r = doc.report || {};
  const s = r.companySummary || {};

  const pdf = async () => {
    setPdfBusy(true);
    try {
      await downloadFile(`/cs/reports/${id}/pdf?cover=${cover}&graph=${withGraph}&aml=${amlDone && withAml}`, `BOND_report_${doc.company_id}_${cover.toUpperCase()}.pdf`);
    } catch (e) { toast.error(errMsg(e)); } finally { setPdfBusy(false); }
  };

    const monitor = async () => {
    try { await api.post('/monitoring/companies', { company_id: doc.company_id, company_name: doc.company_name }); toast.success('Monitoraggio attivato'); setDoc({ ...doc, monitoring: true }); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const tabs = [['overview', 'Sintesi'], ['rating', 'Rating'], ['financial', 'Bilanci'], ['people', 'Soci e dirigenti'], ['group', 'Gruppo'], ['negative', 'Negatività'], ['documents', 'Documenti ufficiali'], ['raw', 'Tutti i dati']];

  return (
    <div>
      <div className="flex items-center justify-between border border-[#1F242C] px-6 py-4 mb-10 portal-rise" style={{ backgroundColor: '#0B0D10' }} data-testid="report-brand-bar">
        <img src="/assets/BOND_logo_white.png" alt="BOND" className="h-8 w-auto" data-testid="report-brand-logo" />
        <span className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.3em] text-[#5A5F67]">Business Information Report</span>
        <img src="/assets/smart_info_logo.png" alt="Smart Info" className="h-7 w-auto" />
      </div>
      <PageHeader kicker={`Report ${doc.template} · ${fmtDateTime(doc.created_at)} · ${eur(doc.price_cents)}`} title={<span data-testid="report-company-name">{doc.company_name}</span>}>
        <select value={cover} onChange={(e) => setCover(e.target.value)} className="h-10 px-3 bg-[#0B0D10] border border-[#1F242C] text-[#E5E5E5] text-sm rounded-sm" data-testid="report-cover-select">
          <option value="a">Copertina A · Classica</option>
          <option value="b">Copertina B · Executive scura</option>
          <option value="c">Copertina C · Fascia laterale</option>
        </select>
        <label className="h-10 px-3 flex items-center gap-2 border border-[#1F242C] rounded-sm text-sm text-[#C9CCD1] cursor-pointer" data-testid="report-graph-toggle">
          <input type="checkbox" checked={withGraph} onChange={(e) => setWithGraph(e.target.checked)} className="accent-[#D4AF37]" data-testid="report-graph-checkbox" /> Includi grafo
        </label>
        {amlDone && (
          <label className="h-10 px-3 flex items-center gap-2 border border-[#1F242C] rounded-sm text-sm text-[#C9CCD1] cursor-pointer" data-testid="report-aml-toggle">
            <input type="checkbox" checked={withAml} onChange={(e) => setWithAml(e.target.checked)} className="accent-[#D4AF37]" data-testid="report-aml-checkbox" /> Includi reputational check
          </label>
        )}
        <Button onClick={pdf} disabled={pdfBusy} className={`${ghostBtn} h-10`} data-testid="report-pdf-btn">{pdfBusy ? <Loader2 size={15} className="animate-spin" /> : <><Download size={15} className="mr-2" />Scarica PDF BOND</>}</Button>
        <Button onClick={() => navigate(`/portal/grafo?report=${doc.id}`)} className={`${ghostBtn} h-10`} data-testid="report-graph-btn"><Share2 size={15} className="mr-2" />Grafo</Button>
        <FromReportDialog reportId={doc.id} />
        <PropertyButton name={doc.company_name} code={r.alternateSummary?.vatRegistrationNumber || r.alternateSummary?.taxCode} kind="company" testid="report-property-btn" />
        <Button onClick={() => navigate(`/portal/dossier?report=${doc.id}`)} className={`${ghostBtn} h-10`} data-testid="report-dossier-btn"><FolderOpen size={15} className="mr-2" />Crea dossier</Button>
        {doc.monitoring
          ? <Link to="/portal/monitoraggio" className="h-10 px-4 flex items-center text-sm border border-[#2E9E6A]/50 text-[#5FC18F] rounded-sm" data-testid="report-monitor-active"><Radar size={15} className="mr-2" />In monitoraggio</Link>
          : <Button onClick={monitor} className={`${goldBtn} h-10`} data-testid="report-monitor-btn"><Radar size={15} className="mr-2" />Riattiva monitoraggio</Button>}
      </PageHeader>
      <p className="text-xs font-mono text-[#5A5F67] -mt-6 mb-8 flex items-center gap-2"><FileText size={12} /> {doc.company_id} · Safe {s.companyNumber} · REA {s.companyRegistrationNumber} · {s.companyStatus?.description}</p>
      <Tabs defaultValue="overview">
        <TabsList className="bg-transparent border-b border-[#1F242C] rounded-none w-full justify-start h-auto p-0 mb-8 overflow-x-auto flex-nowrap">
          {tabs.map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`report-tab-${v}`} className="rounded-none px-4 py-3 text-sm text-[#8A8F97] focus-visible:ring-0 focus-visible:ring-offset-0 outline-none data-[state=active]:bg-transparent data-[state=active]:text-[#D4AF37] data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-[#D4AF37] whitespace-nowrap">{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview"><Overview report={r} /></TabsContent>
        <TabsContent value="rating"><RatingSection report={r} /></TabsContent>
        <TabsContent value="financial"><FinancialSection report={r} /></TabsContent>
        <TabsContent value="people"><PeopleSection report={r} /></TabsContent>
        <TabsContent value="group"><ControlChainSection chain={doc.control_chain} /><GroupSection report={r} /></TabsContent>
        <TabsContent value="negative"><NegativeSection report={r} /></TabsContent>
        <TabsContent value="documents"><DocumentsPanel companyId={doc.company_id} companyName={doc.company_name} /></TabsContent>
        <TabsContent value="raw"><RawData report={r} /></TabsContent>
      </Tabs>
    </div>
  );
}
